// JavaScript Document

// Scripts written by Jacob Bearce @ Weblinx, Inc.

import handorgel from "handorgel";

const DEBOUNCE = (func, wait) => {
    let timeout;

    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
    };
};

const UPDATE_VIA_HASH = (accordion, hash) => {
    const TARGET_ID  = hash.replace(/^#/, "");
    const OLD_TARGET = document.querySelector(".hash-target");

    if (OLD_TARGET) {
        OLD_TARGET.classList.remove("hash-target");
    }

    requestAnimationFrame(() => {
        if (accordion && accordion.folds) {
            accordion.folds.forEach((fold) => {
                if (! fold.handorgel) {
                    return;
                }

                if ((TARGET_ID.match(/^[A-Za-z]/) && fold.content.querySelector(`#${TARGET_ID}`)) || fold.header.dataset.id === TARGET_ID) {
                    fold.open();
                }

                if (fold.header.dataset.id === TARGET_ID) {
                    fold.header.classList.add("hash-target");
                } else if (hash.match(/^#[A-Za-z]/)  && fold.content.querySelector(hash)) {
                    fold.content.querySelector(hash).classList.add("hash-target");
                }
            });
        }
    });
};

const INIT = (element) => {
    const PARAMS = new URLSearchParams(window.location.search);

    const TABS   = element.closest(".weblinx-tab-container");
    const SELECT = TABS ? TABS.querySelector(".weblinx-tab-select") : null;

    const ACCORDION = new handorgel(element, {
        headerElements:       ".weblinx-accordion__header",
        contentElements:      ".weblinx-accordion__panel",
        headerOpenClass:      "weblinx-accordion__header--state-open",
        contentOpenClass:     "weblinx-accordion__panel--state-open",
        headerOpenedClass:    "weblinx-accordion__header--state-opened",
        contentOpenedClass:   "weblinx-accordion__panel--state-opened",
        headerFocusClass:     "weblinx-accordion__header--state-focus",
        contentFocusClass:    "weblinx-accordion__panel--state-focus",
        headerDisabledClass:  "weblinx-accordion__header--state-disabled",
        contentDisabledClass: "weblinx-accordion__panel--state-disabled",
        multiSelectable:      TABS ? false : true,
        collapsible:          TABS ? false : true,
    });

    if (! ACCORDION.folds) {
        return;
    }

    /**
     * Open accordions targeted by Relevanssi
     */
    if (PARAMS.get("highlight")) {
        ACCORDION.folds.forEach((fold) => {
            if (fold.content.querySelector("mark")) {
                fold.open();
            }
        });
    }

    /**
     * Open accordions targeted by a URL hash on hashchange
     */
    window.addEventListener("hashchange", () => {
        UPDATE_VIA_HASH(ACCORDION, window.location.hash);
    });

    /**
     * Open accordions targeted by a URL hash on load
     */
    if (window.location.hash) {
        UPDATE_VIA_HASH(ACCORDION, window.location.hash);
    }

    /**
     * Scroll to hash when an accordion finishes opening
     */
    ACCORDION.on("fold:opened", (fold) => {
        requestAnimationFrame(() => {
            if (window.location.hash && (fold.header.classList.contains("hash-target") || fold.content.querySelector(".hash-target") || (window.location.hash.match(/^#[A-Za-z]/) && (fold.header.querySelector(window.location.hash) || fold.content.querySelector(window.location.hash))))) {
                document.querySelector(".hash-target").scrollIntoView();
            } else {
                window.location.hash = fold.header.dataset.id;
            }
        });
    });

    /**
     * Fix Firefox bug
     */
    ACCORDION.on("fold:close", (fold) => {
        setTimeout(() => {
            fold.header.classList.remove("weblinx-accordion__header--open");
            fold.content.classList.remove("weblinx-accordion__content--open");
        }, 100);
    });

    /** Handle tabs and select when present */
    if (TABS || SELECT) {
        // Open the first tab if none are open
        if (! ACCORDION.folds.some((fold) => fold.expanded)) {
            ACCORDION.folds[0].open();
        }
    }

    /**
     * Handle tabs when present
     */
    if (TABS) {
        const BUTTONS    = TABS.querySelectorAll(".weblinx-tab-list__button");
        const BREAKPOINT = TABS.dataset.breakpoint !== undefined ? parseInt(TABS.dataset.breakpoint) : null;

        if (BUTTONS.length) {
            TABS.setAttribute("role", "tablist");
        }

        ACCORDION.folds.forEach((fold, index) => {
            if (! BUTTONS.length || ! BUTTONS[index]) {
                return;
            }

            BUTTONS[index].setAttribute("role", "tab");
            BUTTONS[index].setAttribute("aria-controls", fold.content.id);

            BUTTONS[index].setAttribute("aria-selected", fold.header.classList.contains("weblinx-accordion__header--state-open") ? "true" : "false");
            BUTTONS[index].setAttribute("tabindex", fold.header.classList.contains("weblinx-accordion__header--state-open") ? "0" : "-1");

            // ID = handorgel1-fold1-button
            BUTTONS[index].id = `${element.id}-fold${index + 1}-button`;

            BUTTONS[index].addEventListener("click", () => {
                BUTTONS[index].setAttribute("aria-selected", "true");
                BUTTONS[index].removeAttribute("tabindex");

                fold.open();

                ACCORDION.folds.forEach((otherFold, otherIndex) => {
                    if (otherFold !== fold) {
                        BUTTONS[otherIndex].setAttribute("aria-selected", "false");
                        BUTTONS[otherIndex].setAttribute("tabindex", "-1");
                    }
                });
            }, { passive: true });

            // Add keyboard navigation
            BUTTONS[index].addEventListener("keydown", (event) => {
                switch (event.key) {
                case "ArrowRight":
                    event.preventDefault();
                    BUTTONS[(index + 1) % BUTTONS.length].focus();
                    break;
                case "ArrowLeft":
                    event.preventDefault();
                    BUTTONS[(index - 1 + BUTTONS.length) % BUTTONS.length].focus();
                    break;
                case "Home":
                    event.preventDefault();
                    BUTTONS[0].focus();
                    break;
                case "End":
                    event.preventDefault();
                    BUTTONS[BUTTONS.length - 1].focus();
                    break;
                case "Enter":
                case " ":
                    event.preventDefault();
                    BUTTONS[index].click();
                    break;
                }
            });
        });

        /**
         * Handle select when present
         */
        if (SELECT) {
            SELECT.setAttribute("role", "tablist");

            const OPTIONS = Array.from(SELECT.options);

            OPTIONS.forEach((option) => {
                option.setAttribute("role", "tab");
            });

            SELECT.addEventListener("change", () => {
                const SELECTED_OPTION = OPTIONS.find((option) => option.selected);
                const SELECTED_INDEX  = OPTIONS.indexOf(SELECTED_OPTION);
                const SELECTED_FOLD   = ACCORDION.folds[SELECTED_INDEX];

                if (SELECTED_FOLD) {
                    SELECTED_FOLD.open();
                }
            }, { passive: true });
        }

        // Update aria-selected and select when a fold is opened or closed
        ACCORDION.on("fold:opened", (fold) => {
            const index = Array.from(ACCORDION.folds).indexOf(fold);

            /**
             * Handle buttons
             */
            if (BUTTONS.length && BUTTONS[index]) {
                BUTTONS[index].setAttribute("aria-selected", "true");
                BUTTONS[index].removeAttribute("tabindex");
            }

            /**
             * Handle select
             */
            if (SELECT) {
                const OPTIONS = Array.from(SELECT.options);

                OPTIONS.forEach((option) => {
                    option.selected = option.value === fold.header.dataset.id;
                });

                // If no button is found, select the first option
                if (! OPTIONS.some((option) => option.selected)) {
                    OPTIONS[0].selected = true;
                }
            }
        });

        ACCORDION.on("fold:closed", (fold) => {
            const index = Array.from(ACCORDION.folds).indexOf(fold);

            /**
             * Handle buttons
             */
            if (BUTTONS.length && BUTTONS[index]) {
                BUTTONS[index].setAttribute("aria-selected", "false");
                BUTTONS[index].setAttribute("tabindex", "-1");
            }

            /**
             * Handle select
             */
            if (SELECT) {
                // switch to first option if no folds are open
                if (! ACCORDION.folds.some((fold) => fold.expanded)) {
                    SELECT.options[0].selected = true;
                }
            }
        });

        // On load and window resize, check if all tabs can fit within the container
        const CHECK_TABS = () => {
            const MENU  = TABS.querySelector(".weblinx-tab-list");

            if (! MENU) {
                return;
            }

            /**
             * Set active state so we can measure
             */
            MENU.classList.add("weblinx-tab-list--state-active");

            const GAP = getComputedStyle(MENU).gap.replaceAll("px", "").split(" ");

            const GAP_WIDTH       = GAP.length === 2 ? parseInt(GAP[1]) : parseInt(GAP[0]);
            const CONTAINER_WIDTH = TABS.offsetWidth;
            const ELEMENT_WIDTH   = Array.from(BUTTONS).reduce((total, button) => total + button.offsetWidth + GAP_WIDTH, 0) - GAP_WIDTH - 2; // -2 accounts for rounding errors

            if ((BREAKPOINT !== null && BREAKPOINT > CONTAINER_WIDTH) || (BREAKPOINT === null && ELEMENT_WIDTH > CONTAINER_WIDTH)) {
                MENU.classList.remove("weblinx-tab-list--state-active");

                if (SELECT) {
                    SELECT.classList.add("weblinx-tab-select--state-active");
                }

                ACCORDION.folds.forEach((fold, index) => {
                    fold.content.setAttribute("role", "region");
                    fold.content.setAttribute("aria-labelledby", ACCORDION.folds[index].header.id);
                });
            } else {
                if (SELECT) {
                    SELECT.classList.remove("weblinx-tab-select--state-active");
                }

                ACCORDION.folds.forEach((fold, index) => {
                    fold.content.setAttribute("role", "tabpanel");
                    fold.content.setAttribute("aria-labelledby", BUTTONS[index] ? BUTTONS[index].id : "");
                });
            }
        };

        document.addEventListener("DOMContentLoaded", CHECK_TABS());
        window.addEventListener("resize", DEBOUNCE(CHECK_TABS, 200));
    }
};

const OBSERVER = new MutationObserver((mutations) => {
    // Temporarily disconnect the observer while we make changes
    OBSERVER.disconnect();

    mutations.forEach((mutation) => {
        // Check for newly added accordions
        mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement === false) {
                return;
            }

            if (! (node.classList.contains("acf-block-preview") || node.classList.contains("wp-block-acf-weblinx-accordion"))) {
                return;
            }

            // if (node.querySelector(".components-placeholder")) {
            //     return;
            // }

            const CONTAINER = node.closest(".weblinx-accordion-container") || node.querySelector(".weblinx-accordion-container");

            if (CONTAINER === null) {
                return;
            }

            if (CONTAINER.handorgel) {
                CONTAINER.handorgel.destroy();
            }

            INIT(CONTAINER);
        });

        // Check for removed headers
        mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement === false) {
                return;
            }

            if (! node.classList.contains("wp-block-acf-weblinx-accordion")) {
                return;
            }

            const CONTAINER = node.closest(".weblinx-accordion-container") || node.querySelector(".weblinx-accordion-container");

            if (CONTAINER === null) {
                return;
            }

            if (CONTAINER.handorgel) {
                CONTAINER.handorgel.destroy();
            }

            setTimeout(() => {
                INIT(CONTAINER);
            }, 500);
        });
    });

    // Reconnect the observer
    OBSERVER.observe(document.body, { childList: true, subtree: true });
});

document.addEventListener("DOMContentLoaded", () => {
    const ACCORDIONS = document.querySelectorAll(".weblinx-accordion-container");

    ACCORDIONS.forEach((accordion) => {
        if (! accordion.handorgel) {
            INIT(accordion);
        }
    });

    OBSERVER.observe(document.body, { childList: true, subtree: true });
});
